import React from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface AppDialogProps {
  open: boolean;
  /** Omit for a dialog that must be completed: no close button, and neither
   *  the backdrop nor Escape closes it. */
  onClose?: () => void;
  title: string;
  titleAdornment?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  actions?: React.ReactNode;
  /** Stack above any other open dialog (a dialog that must be dealt with first). */
  onTop?: boolean;
}

export default function AppDialog({
  open, onClose, title, titleAdornment, maxWidth = 'sm', children, actions, onTop,
}: AppDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      sx={onTop ? { zIndex: (theme) => theme.zIndex.modal + 1 } : undefined}
      // Use MUI's default portal-into-body + fixed positioning. The previous
      // setup portaled into `#page-content` AND forced `position: absolute`,
      // which positions the dialog inside the absolute coordinate system of
      // the page-content block. On long pages (courses, events) where the
      // user has scrolled, the backdrop still covered the viewport but the
      // dialog sat at scroll-top — invisible. Fixed positioning keeps it
      // centered in the viewport regardless of scroll.
      slotProps={{
        paper: { elevation: 0, sx: { border: '1px solid', borderColor: 'divider' } },
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.45)' } },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
          <span>{title}</span>
          {titleAdornment}
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      {/*
        MUI ships a global rule `.MuiDialogTitle-root + .MuiDialogContent-root
        { padding-top: 0 }` so titled dialogs don't double-pad. That rule is
        fine for plain-text content, but breaks outlined-label TextFields at
        the very top of the body: the floating label sits ~9px ABOVE the
        input border, and with pt:0 + overflow-y:auto the label's top half
        gets clipped (visible on Edit Host, Edit Venue, Add Instructor, etc.).
        Restoring 16px of top padding gives the label room to breathe without
        making text-only dialogs feel padded.
      */}
      <DialogContent sx={{ pt: 2 }}>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
